import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { Web } from 'sip.js';
import { Room, RoomEvent, ParticipantEvent, Track, createLocalTracks } from 'livekit-client';

import EventBus from '../../event-bus';
import callHungUpSoundSrc from '@/assets/sound/call_hangup.mp3';
import callReceivedSoundSrc from '@/assets/sound/call_answered.mp3';
import { CallRecorder } from '@/services/callRecording/recorder';
import {
  isCallCenterBackupEnabled,
  callCenterBackupDirection,
} from '@/services/callRecording/config';

const callHungUpSound   = new Audio(callHungUpSoundSrc);
const callReceivedSound = new Audio(callReceivedSoundSrc);

// Backup de grabación por llamada (solo compañías en allowlist, LiveKit).
// Vive a nivel de módulo porque MediaRecorder/AudioContext no van en el state.
// El agente permanece en la room entre llamadas: start/stop es por llamada, no por sesión.
let recorder = null;
let backupEnabled = false;
let backupLocalTrack = null;
let pendingRemoteTrack = null;
let recordingBegun = false;

async function stopCallCenterBackup() {
  if (recorder) {
    try {
      await recorder.stop();
    } catch (e) {
      console.warn('CallRecorder: error al detener backup call center', e);
    }
    recorder = null;
  }
  pendingRemoteTrack = null;
  recordingBegun = false;
}

async function tryStartCallCenterBackup(currentCall) {
  if (!backupEnabled || recordingBegun) {
    return;
  }
  if (!currentCall || !currentCall.call_uuid) {
    return;
  }
  if (!backupLocalTrack || !pendingRemoteTrack) {
    return;
  }

  recordingBegun = true;
  recorder = new CallRecorder();
  recorder.setRemoteTrack(pendingRemoteTrack);

  try {
    await recorder.start({
      localTrack: backupLocalTrack.mediaStreamTrack,
      meta: {
        call_uuid: currentCall.call_uuid,
        company_id: currentCall.company_id || Number(Vue.auth.user().company.id),
        recorded_at: moment.utc().toISOString(),
        direction: callCenterBackupDirection(currentCall.type_id),
      },
    });
  } catch (e) {
    console.error('CallRecorder: fallo al iniciar backup call center', e);
    recorder = null;
    recordingBegun = false;
  }
}

export default {
  async getSipAuth({}, callListId) {
    const defaultMessage = 'Ocurrio un error obteniendo el token, intente de nuevamente.';
    return Vue.axios.post('/call_center/token', {
      call_list_id: callListId,
    })
      .then(({ data }) => ({
          err: null,
        data,
      }))
      .catch(({ response: { data: { message = defaultMessage } = { message: defaultMessage } } = { data: { message: defaultMessage } } }) => ({
        err: message,
        data: null,
      }));
  },
  async createSoftphone({ commit, dispatch }, { delegate, callListId, reconnectionDelay, reconnectionAttempts, extraHeaders }) {
    dispatch('callTimeAndAlert/getCallDurationData', null, { root: true }); // TODO: cambiar para recibirlo entre los datos de conexión
    const audioAccess = await navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        return true;
      })
      .catch(() => false);

    if (!audioAccess) {
      throw Error('No hay acceso al microfono, no se puede conectar.');
    }

    const { err, data: auth } = await dispatch('getSipAuth', callListId);
    if (err) {
      throw Error(err);
    }

    const userAgentOptions = {
      authorizationUsername: auth.sip_auth.username,
      authorizationPassword: auth.sip_auth.password,
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 500,
        peerConnectionConfiguration: {
          iceTransportPolicy: 'relay',
          iceServers: auth.iceServers,
        },
      },
    };

    const server = auth.sip_server || process.env.CALL_SERVER;

    const email = (Vue.auth.user().nexus_email || Vue.auth.user().email).split('@')[0];
    const softPhone = new Web.SimpleUser(`wss://${server}`, {
      aor: `sip:${email}@${server}`,
      media: {
        remote: {
          audio: document.getElementById('callCenterAudio'),
        },
      },
      delegate,
      userAgentOptions,
      reconnectionDelay,
      reconnectionAttempts,
    });
    softPhone.sessionManager.options.optionsPingInterval = 30;
    softPhone.sessionManager.userAgent.configuration.sendInitialProvisionalResponse = false;

    const connection = await softPhone.connect()
      .then(() => true)
      .catch((err) => {
        console.error('ERROR CONNECTING TO CALL SERVER', err);
        return false;
      });

    if (!connection) {
      throw Error('Ocurrio un error conectando con el servidor, intente nuevamente.');
    }

    const register = await softPhone.register({
      requestOptions: {
        extraHeaders,
      },
    })
      .then(() => true)
      .catch((err) => {
        console.error('ERROR REGISTERING TO SERVER', err);
        return false;
      });

    if (!register) {
      throw Error('Ocurrio un error al registrarse en el servidor, intente nuevamente.');
    }

    return softPhone;
  },
  hangupLivekit() {
    return Vue.axios.post('/call_center/livekit/hangup')
    .catch(({ response }) => {
      console.log('ERR COLGANDO LA LLAMADA', response);
    });
  },
  hangupCall({ state, rootState, dispatch }) {
    if (!state.softPhone && !state.room) {
      return;
    }

    if (state.status !== 3) {
      return;
    }

    if (Number(state.currentCall.company_id) === 86496225) {
      const callUuid = state.currentCall.call_uuid;
      const nsId = state.currentCall.id;
      const pending = rootState.dialer.callsToSave.some(
        item => item.call_uuid === callUuid || item.id === nsId
      );
      if (pending) {
        EventBus.$emit('modal', 'Debe guardar el estatus antes de colgar la llamada');
        return;
      }
    }

    if (state.softPhone) {
      return state.softPhone.hangup();
    }

    return dispatch('hangupLivekit');
  },
  muteSoftphone({ state }) {
    if (state.softPhone.isMuted()) {
      return state.softPhone.unmute();
    }

    return state.softPhone.mute();
  },
  muteLivekit({ state }) {
    if (state.audioTrack.isMuted) {
      return state.audioTrack.unmute();
    }

    return state.audioTrack.mute();
  },
  muteCall({ state, dispatch }) {
    if (!state.softPhone && !state.room) {
      return;
    }

    if (state.status !== 3) {
      return;
    }

    if (state.softPhone) {
      return dispatch('muteSoftphone');
    }

    return dispatch('muteLivekit');
  },
  async requestDisconnection({ state, commit, dispatch }, disconnectReason) {
    if (!state.status || !state.keepCalling) {
      return;
    }

    const result = await Vue.axios.post('/call_center/request_disconnection', disconnectReason)
      .then(() => true)
      .catch((error) => {
        console.error('Error solicitando desconexion al API', error);
        return false;
      });

    if (!result) {
      EventBus.$emit('modal', 'Ocurrio un error al hacer la solicitud de desconexion, intente nuevamente.');
      return false;
    }

    commit('SET_KEEP_CALLING', false);
    commit('SET_RETRY_CONNECTION', false);
    commit('SET_DISCONNECT_REASON', disconnectReason);

    if (state.status === 1) {
      return dispatch('performDisconnection');
    }
  },
  softphoneDisconnect({ state }) {
    return state.softPhone.disconnect()
      .then(() => (new Promise((resolve) => setTimeout(resolve, 3500))));
  },
  async performDisconnection({ state, dispatch }) {
    if (state.room) {
      await stopCallCenterBackup();
      return state.room.disconnect();
    }

    let extraHeaders = [
      'Expires: 0',
      `X-Nexus-Email: ${Vue.auth.user().nexus_email}`,
    ];

    if ((state.disconnectReason || {}).id) {
      extraHeaders = [
        ...extraHeaders,
        `X-Disconnect-Reason-Id: ${state.disconnectReason.id}`,
        `X-Disconnect-Reason-Name: ${state.disconnectReason.name}`,
      ];
    }

    return new Promise((resolve, reject) => {
      state.softPhone.unregister({
        requestOptions: {
          extraHeaders,
        },
        requestDelegate: {
          onAccept(response) {
            resolve(response);
          },
          onReject(response) {
            reject(response);
          },
        }
      })
    })
      .catch(() => {})
      .then(() => dispatch('softphoneDisconnect'))
      .then(() => dispatch('postDisconnection'));
  },
  postDisconnection({ commit }) {
    stopCallCenterBackup();
    backupEnabled = false;
    backupLocalTrack = null;
    commit('SET_STATUS', 0);
    commit('SET_ROOM', null);
    commit('SET_SOFTPHONE', null);
    commit('SET_NO_CALLS', false);
    commit('SET_CURRENT_CALL', {});
    commit('SET_AUDIO_TRACK', null);
    commit('SET_RECONNECTING', false);
    commit('SET_DISCONNECT_REASON', {});
    commit('SET_DISCONNECTION_TIMEOUT', '');
    commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', 0, { root: true });
    return Vue.router.push({
      name: 'CallList',
    });
  },
  handleConnectionLost({ state, commit, dispatch }) {
    if (!state.keepCalling || state.disconnectionTimeout || state.noCalls) {
      return;
    }

    commit('SET_RECONNECTING', true);
    commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', 0, { root: true });

    if (state.status === 3) { // TODO, CHECK SI ESTOY EN LLAMADA Y RECONECTA, ESTO DEBE IR?
      Vue.router.push({
        name: 'CallCenterWaitV2',
      });
    }

    const timeout = setTimeout(() => {
      if (state.status !== 0 && state.reconnecting) {
        EventBus.$emit('modal', 'Se perdio la conexión con el servidor de llamadas');
        return dispatch('postDisconnection');
      }
    }, ((state.reconnectionAttempts * state.reconnectionDelay) + 3) * 1000);
    commit('SET_DISCONNECTION_TIMEOUT', timeout);
  },
  startCallCenter({ state, commit, dispatch, rootState }, { callListId, maxCommitmentDays }) {
    commit('SET_LOADING', true);

    const delegate = {
      onRegistered() {
        if (state.reconnecting) {
          commit('SET_RECONNECTING', false);
          commit('SET_DISCONNECTION_TIMEOUT', '');
          commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
        }

        if (state.status > 0) {
          return;
        }

        commit('SET_STATUS', 1);
        commit('SET_KEEP_CALLING', true);
        commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
        Vue.router.push({
          name: 'CallCenterWaitV2',
        });
      },
      async onCallReceived() {
        // TO DO, checar si esta en llamada ya, si es asi rechazar

        const answer = await state.softPhone.answer()
          .then(() => true)
          .catch((err) => {
            console.error('ERROR EN EL ANSWER', err);
            return false;
          });

        if (!answer) {
          return;
        }

        const { softPhone: { session: { incomingInviteRequest: { message: { headers } } } } } = state;
        const typeId = Number(headers['X-Type-Id'][0].raw);

        if (!typeId) {
          return;
        }

        callReceivedSound.play();

        commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
        commit('SET_STATUS', 3);

        let currentCall = {
          phone:                String(headers['X-Phone'][0].raw),
          call_id:              String(headers['Call-ID'][0].raw),
          type_id:              Number(typeId),
          company_id:           Number(Vue.auth.user().company.id),
          channel_id:           5,
          max_commitment_days:  rootState.dialer.maxCommitmentDays,
        };

        if (typeId === 7 || typeId === 9) {
          currentCall.timestamp = String(headers['X-Timestamp'][0].raw);
        }

        if (typeId === 7) {
          currentCall.full_name = 'Desconocido';
        } else {
          currentCall.amount = headers['X-Amount'][0].raw;
          currentCall.account = headers['X-Account'][0].raw;
          currentCall.full_name = headers['X-Full-Name'][0].raw;
          currentCall.customer_id = Number(headers['X-Customer-Id'][0].raw);
        }

        if (typeId === 1 || typeId === 2) {
          currentCall.id = Number(headers['X-Notification-Id'][0].raw);
        }

        if (typeId === 9) {
          currentCall.ns_status = headers['X-Ns-Status'][0].raw;
          currentCall.original_channel_id = Number(headers['X-Original-Channel-Id'][0].raw);
        }

        commit('SET_CURRENT_CALL', currentCall);

        if (typeId === 7) {
          return;
        }

        commit('dialer/ADD_CALL_TO_SAVE', currentCall, { root: true });
        return Vue.router.push(`/customers/${currentCall.customer_id}/show`);
      },
      async onCallHangup() {
        callHungUpSound.play();

        if (state.currentCall.type_id === 7 && !state.currentCall.customer_id && !state.currentCall.fiscal_id) {
          commit('dialer/ADD_CALL_TO_SAVE', state.currentCall, { root: true });
        }

        commit('SET_STATUS', 1);
        commit('SET_CURRENT_CALL', {});

        commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
        await Vue.router.push({
          name: 'CallCenterWaitV2',
        });

        // Si la llamada se cuelga y el usuario no quiere seguir llamando se manda a desconectar
        if (!state.keepCalling) {
          return dispatch('performDisconnection');
        }

        // Si se quedo sin llamadas la campaña mientras el usuario estaba en llamada se desconecta
        if (state.noCalls) {
          EventBus.$emit('modal', 'No hay llamadas');
          return dispatch('performDisconnection');
        }
      },
      onMessageReceived(message) {
        if (
          message !== 'NO_CALLS' 
          && message !== 'ERROR_RECEIVING_CALL' 
          && message !== 'FAILED_CALL'
        ) {
          return;
        }

        let modalMessage = 'No hay llamadas';
        if (message === 'FAILED_CALL') {
          return dispatch('softphoneDisconnect')
            .catch(() => {})
            .then(() => dispatch('startCallCenter', { callListId: state.callListId, maxCommitmentDays: rootState.dialer.maxCommitmentDays }));
        }

        commit('SET_NO_CALLS', true);
        if (state.status === 3) {
          return;
        }

        EventBus.$emit('modal', 'No hay llamadas, desconectando...');
        return dispatch('performDisconnection');
      },
      onServerDisconnect(error) {
        console.error('DESCONECTADO DEL SERVER', error);
        dispatch('handleConnectionLost');
      },
      onUnregistered() {
        console.error('DESREGISTRADO DEL SERVER');
        dispatch('handleConnectionLost');
      },
    };

    return dispatch('createSoftphone', {
      delegate,
      callListId,
      reconnectionDelay: state.reconnectionDelay,
      reconnectionAttempts: state.reconnectionAttempts,
      extraHeaders: [
        'X-Accept-Incoming: true',
        `X-Call-List-Id: ${callListId}`,
        `X-User-Id: ${Vue.auth.user().id}`,
        `X-Company-Id: ${Vue.auth.user().company.id}`,
        `X-Nexus-Email: ${Vue.auth.user().nexus_email}`,
        `X-Country: ${Vue.auth.user().company.country.name}`,
        `X-Country-Id: ${Vue.auth.user().company.country.id}`,
        `X-Full-Name: ${Vue.auth.user().first_name} ${Vue.auth.user().last_name}`,
        `X-Timezone: ${Vue.auth.user().company.country.timezone}`,
      ],
    })
      .then((softPhone) => {
        commit('SET_SOFTPHONE', softPhone);
        commit('SET_CALL_LIST_ID', callListId);
        commit('dialer/SET_MAX_COMMITMENT_DAYS', maxCommitmentDays, { root: true });
      })
      .catch((err) => {
        commit('SET_LOADING', false);
        EventBus.$emit('modal', err.message);
        throw Error(err);
      });
  },
  callReceived({ state, commit, rootState }, attributes) {
    const tmp = _.pickBy(attributes, (value, key) => key.startsWith('body.'));
    const body = _.mapKeys(tmp, (value, key) => key.replace('body.', ''));

    body.call_id = body.call_uuid || attributes['sip.callIDFull'] || attributes['sip.callID'];

    if (body.call_uuid && String((state.currentCall || {}).call_uuid) === String(body.call_uuid)) {
      return;
    }

    console.log('BODY', body);
    const typeId = Number(body.type_id);

    if (!typeId) {
      return;
    }

    commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
    commit('SET_STATUS', 3);

    const currentCall = {
      phone:                String(body.phone),
      call_id:              String(body.call_id),
      type_id:              Number(typeId),
      call_uuid:            body.call_uuid,
      company_id:           Number(Vue.auth.user().company.id),
      channel_id:           5,
      max_commitment_days:  rootState.dialer.maxCommitmentDays,
    };

    if (typeId === 7 || typeId === 9) {
      currentCall.timestamp = String(body.timestamp);
    }

    if (typeId === 7) {
      currentCall.full_name = 'Desconocido';
    } else {
      currentCall.amount = body.amount;
      currentCall.account = body.account;
      currentCall.full_name = body.full_name;
      currentCall.customer_id = body.customer_id;
    }

    if (typeId === 1 || typeId === 2) {
      currentCall.id = body.id;
    }

    if (typeId === 9) {
      currentCall.ns_status = body.ns_status;
      currentCall.original_channel_id = Number(body.original_channel_id);
    }

    commit('SET_CURRENT_CALL', currentCall);

    // Arranca backup si ya llegó el track remoto (TrackSubscribed puede ir antes o después).
    tryStartCallCenterBackup(currentCall);

    if (typeId === 7) {
      return;
    }

    commit('dialer/ADD_CALL_TO_SAVE', currentCall, { root: true });
    return Vue.router.push(`/customers/${currentCall.customer_id}/show`);
  },
  async startLiveKit({ state, commit, dispatch }, { callListId, maxCommitmentDays, auth }) {
    commit('SET_IS_CAMPAIGN_TRANSFER', false);
    commit('SET_LOADING', true);

    const [audioTrack] = await createLocalTracks({
      audio: true,
      video: false,
    });

    // Backup de grabación en call center (allowlist). Precondición dura: si el
    // navegador no puede grabar, no se conecta a la campaña.
    const companyId = Number(Vue.auth.user().company.id);
    backupEnabled = isCallCenterBackupEnabled(companyId);
    backupLocalTrack = audioTrack;
    pendingRemoteTrack = null;
    recordingBegun = false;
    recorder = null;

    if (backupEnabled) {
      const probe = new CallRecorder();
      const canRecord = await probe.preflight();
      if (!canRecord) {
        audioTrack.stop();
        backupEnabled = false;
        backupLocalTrack = null;
        commit('SET_LOADING', false);
        EventBus.$emit('modal', 'No se puede conectar a la campaña: la grabación de respaldo no está disponible en este navegador. Revisa los permisos de almacenamiento e inténtalo nuevamente.');
        throw Error('backup recording preflight failed');
      }
    }

    // TODO: cambiar para recibirlo entre los datos de conexión
    dispatch('callTimeAndAlert/getCallDurationData', null, { root: true });

    if (!auth) {
      let err = null;
      ({ err, data: auth } = await dispatch('getSipAuth', callListId));
      if (err) {
        audioTrack.stop();
        backupEnabled = false;
        backupLocalTrack = null;
        commit('SET_LOADING', false);
        throw Error(err);
      }
    }

    const { server, token, roomName } = auth;

    const room = new Room(roomName);

    room.on(RoomEvent.SignalConnected, async () => {
      console.log('SIGNAL_CONNECTED');

      room.localParticipant.publishTrack(audioTrack).then(() => audioTrack.mute());

      commit('SET_CALL_LIST_ID', callListId);
      commit('dialer/SET_MAX_COMMITMENT_DAYS', maxCommitmentDays, { root: true });

      commit('SET_STATUS', 1);
      commit('SET_ROOM', room);
      commit('SET_KEEP_CALLING', true);
      commit('SET_AUDIO_TRACK', audioTrack);
      commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });

      return Vue.router.push({
        name: 'CallCenterWaitV2',
      });
    });

    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== Track.Kind.Audio) {
        console.log('TRACK', track.kind, 'NOT SUPPORTED');
        return;
      }

      const audioElement = document.getElementById('callCenterAudio');
      const element = track.attach();
      audioElement.appendChild(element);

      // Audio remoto del cliente: guardar track e intentar arrancar backup.
      if (backupEnabled) {
        pendingRemoteTrack = track.mediaStreamTrack;
        tryStartCallCenterBackup(state.currentCall);
      }
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
    });
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('PARTICIPANT_CONNECTED');

      audioTrack.unmute();
      callReceivedSound.play();

      if (participant.attributes['body.type_id']) {
        return dispatch('callReceived', participant.attributes);
      }

      participant.on(ParticipantEvent.AttributesChanged, (attrs) => {
        if (!attrs['body.type_id']) {
          return;
        }

        participant.removeAllListeners(ParticipantEvent.AttributesChanged);

        return dispatch('callReceived', participant.attributes);
      });
    });
    // room.on(RoomEvent.ParticipantMetadataChanged, (prev, { metadata }) => {
    //   console.log('PARTICIPANT_METADATA_CHANGED');
    //   return dispatch('callReceived', metadata);
    // });
    room.on(RoomEvent.ParticipantDisconnected, async () => {
      // Cierra el backup de ESTA llamada; la sesión LiveKit sigue para la siguiente.
      await stopCallCenterBackup();

      audioTrack.mute();
      callHungUpSound.play();

      if (state.currentCall.type_id === 7 && !state.currentCall.customer_id && !state.currentCall.fiscal_id) {
        commit('dialer/ADD_CALL_TO_SAVE', state.currentCall, { root: true });
      }

      commit('SET_STATUS', 1);
      commit('SET_CURRENT_CALL', {});

      commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
      await Vue.router.push({
        name: 'CallCenterWaitV2',
      });

      // Si la llamada se cuelga y el usuario no quiere seguir llamando se manda a desconectar
      if (!state.keepCalling) {
        return dispatch('performDisconnection');
      }

      // Si se quedo sin llamadas la campaña mientras el usuario estaba en llamada se desconecta
      if (state.noCalls) {
        EventBus.$emit('modal', 'No hay llamadas');
        return dispatch('performDisconnection');
      }
    });
    room.on(RoomEvent.SignalReconnecting, () => {
      console.log('SIGNAL_RECONNECTING');
      if (state.reconnecting) {
        return;
      }

      commit('SET_RECONNECTING', true);
    });
    room.on(RoomEvent.Disconnected, () => {
      console.log('DISCONNECTED');
      if (state.reconnecting) {
        EventBus.$emit('modal', 'Se perdio la conexión con el servidor de llamadas');
      }

      return dispatch('postDisconnection');
    });
    room.on(RoomEvent.Reconnected, () => {
      console.log('RECONNECTED');
      commit('SET_RECONNECTING', false);
      commit('callTimeAndAlert/SET_CURRENT_STATUS_START_TIME', moment.utc().unix(), { root: true });
    });
    room.on(RoomEvent.DataReceived, (payload) => {
      const data = Buffer.from(payload).toString();
      console.log('DATA RECEIVED', data);

      if (data !== 'NO_CALLS' && data !== 'CHANGE_CALL_LIST') {
        return;
      }

      if (data === 'CHANGE_CALL_LIST') {
        commit('SET_IS_CAMPAIGN_TRANSFER', true);
        return dispatch('performDisconnection')
        .then(() => {
          Vue.router.push({
            name: 'ConnectCampaign',
          });
        });
      }

      commit('SET_NO_CALLS', true);
      if (state.keepCalling) {
        commit('SET_RETRY_CONNECTION',true);
      }
      if (state.status === 3) {
        return;
      }

      EventBus.$emit('modal', 'No hay llamadas, desconectando...');
      return dispatch('performDisconnection');
    });

    // Force TURN
    const connectOptions = {
      rtcConfig: {
        iceTransportPolicy: 'relay',
      },
    };

    const result = await room.connect(server, token, connectOptions)
      .then(() => ({ success: true }))
      .catch(err => ({ err }));

    if (result.err) {
      audioTrack.stop();
      backupEnabled = false;
      backupLocalTrack = null;
      commit('SET_LOADING', false);
      throw Error(result.err);
    }
  },
};



// WEBPACK FOOTER //
// ./src/store/dialer_v2/actions.js